import type { InvoiceCandidatesInput, InvoiceCandidatesRecord, InvoiceCandidateStatus } from '@navio/nsdk';
import { type FilterOption, filterUnitFor, mapFilterControls, useFilterUnits } from 'noddi-ui-web';

import { EXCLUSION_REASON_LABELS, ORDER_TYPE_OPTIONS, WILL_BE_BILLED } from './invoiceCandidateLabels';

// Customer and seller frame the query rather than filter it, and search stays with pagination.
type InvoiceCandidateFilterInput = Omit<
  InvoiceCandidatesInput,
  'userGroupId' | 'serviceOrganizationId' | 'serviceDepartmentIds' | 'search'
>;

const invoiceCandidateFilterUnit = filterUnitFor<InvoiceCandidateFilterInput>();

const STATUS_FILTER_OPTIONS: FilterOption<InvoiceCandidateStatus>[] = [
  { value: WILL_BE_BILLED, label: 'Will be billed' },
  ...Object.entries(EXCLUSION_REASON_LABELS).flatMap(([value, label]) =>
    isInvoiceCandidateStatus(value) ? [{ value, label }] : []
  )
];

/** The invoice candidates' order type, status and period filters, plus their facet counts. */
export const useInvoiceCreateFilters = () => {
  const filters = useFilterUnits([
    invoiceCandidateFilterUnit.multiSelect('orderTypes', {
      title: 'Order type',
      description: 'A shop purchase has no booking, so its booking columns are empty.',
      options: ORDER_TYPE_OPTIONS.map(({ value, label, icon }) => ({ value, label, icon }))
    }),
    // Defaults to what the screen is for: the orders that can be billed. On a
    // real customer most of the period is already invoiced or card-paid, so the
    // unfiltered list buries the few rows the operator came to tick. The other
    // members still answer "why is this one not here?" -- they are one click away
    // in a filter whose counts name every reason, and the excluded total stays on
    // the table's own description.
    invoiceCandidateFilterUnit.multiSelect('statuses', {
      title: 'Status',
      description: 'Narrow to what will be billed, or to one reason something will not be.',
      options: STATUS_FILTER_OPTIONS,
      defaults: [WILL_BE_BILLED]
    }),
    // The rows carry a completion date, a fulfillment date and a creation date;
    // the range filters the one the period is billed on.
    invoiceCandidateFilterUnit.toolbarDateRange(
      { from: 'fromDate', to: 'toDate' },
      { placeholder: 'Billable date range' }
    )
  ]);

  // Facet counts come from the response, counted before the status and type
  // filters apply. Deriving them from `rows` would collapse every other option
  // to zero the moment one is picked, which hides the way back.
  const withCounts = (candidates: InvoiceCandidatesRecord | undefined) => {
    const countsByParam: Record<string, Map<string | number, number>> = {
      orderTypes: new Map((candidates?.orderTypeCounts ?? []).map(({ orderType, count }) => [orderType, count])),
      statuses: new Map((candidates?.statusCounts ?? []).map(({ status, count }) => [status, count]))
    };
    return mapFilterControls(filters, (control) => {
      const counts = countsByParam[control.id];
      return counts && control.type === 'multi-select'
        ? {
            ...control,
            options: control.options.map((option) => ({ ...option, count: counts.get(option.value) ?? 0 }))
          }
        : control;
    });
  };

  return { filters, withCounts };
};

function isInvoiceCandidateStatus(value: string): value is InvoiceCandidateStatus {
  return value in EXCLUSION_REASON_LABELS || value === WILL_BE_BILLED;
}
